import { useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, errMsg } from '../lib/api';
import { useMe, useTenant, useTid } from '../lib/hooks';
import { useUploads } from '../lib/uploader';
import { TINTS } from '../lib/format';
import { Avatar, Button, Field, Loading, Sheet } from './ui';
import { PasswordInput } from './fields';
import { useToast } from './feedback';

export function tenantTint(id: string, list: Array<{ id: string }> | undefined) {
  const i = list?.findIndex((t) => t.id === id) ?? -1;
  return TINTS[(i < 0 ? 0 : i) % TINTS.length];
}

function ChangePassword({ onDone }: { onDone: () => void }) {
  const toast = useToast();
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [conf, setConf] = useState('');
  const save = useMutation({
    mutationFn: () => api.post('/api/me/password', { atual, nova }),
    onSuccess: () => { toast('Senha alterada. Outros aparelhos vão precisar entrar de novo.'); onDone(); },
    onError: (e) => toast(errMsg(e), 'err'),
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (nova.length < 6) return toast('A nova senha precisa ter ao menos 6 caracteres', 'err');
        if (nova !== conf) return toast('As senhas novas não conferem', 'err');
        save.mutate();
      }}
    >
      <Field label="Senha atual"><PasswordInput autoComplete="current-password" value={atual} onChange={(e) => setAtual(e.target.value)} /></Field>
      <Field label="Nova senha" hint="Pelo menos 6 letras ou números."><PasswordInput autoComplete="new-password" value={nova} onChange={(e) => setNova(e.target.value)} /></Field>
      <Field label="Repita a nova senha"><PasswordInput autoComplete="new-password" value={conf} onChange={(e) => setConf(e.target.value)} /></Field>
      <Button variant="dark" size="lg" block type="submit" loading={save.isPending}>Alterar senha</Button>
      <Button size="lg" block type="button" style={{ marginTop: 8 }} onClick={onDone}>Voltar</Button>
    </form>
  );
}

export function AccountSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const me = useMe().data;
  const qc = useQueryClient();
  const nav = useNavigate();
  const toast = useToast();
  const [view, setView] = useState<'dados' | 'senha'>('dados');
  const [nome, setNome] = useState(me?.user.nome ?? '');
  const [crn, setCrn] = useState(me?.user.crn ?? '');
  const save = useMutation({
    mutationFn: () => api.patch('/api/me', { nome: nome.trim(), crn: crn.trim() || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['me'] }); toast('Dados atualizados'); onClose(); },
    onError: (e) => toast(errMsg(e), 'err'),
  });
  const logout = async () => {
    await api.post('/api/auth/logout').catch(() => undefined);
    qc.clear();
    nav('/entrar', { replace: true });
  };
  return (
    <Sheet open={open} onClose={onClose} title={view === 'senha' ? 'Alterar senha' : 'Sua conta'} subtitle={me?.user.email}>
      {view === 'senha' ? (
        <ChangePassword onDone={() => setView('dados')} />
      ) : (
        <>
          <Field label="Nome completo"><input className="input" value={nome} onChange={(e) => setNome(e.target.value)} autoComplete="name" /></Field>
          <Field label="CRN" hint="Aparece nos relatórios que você assina."><input className="input mono" value={crn} onChange={(e) => setCrn(e.target.value)} placeholder="CRN-3 41872" /></Field>
          <Button variant="dark" size="lg" block loading={save.isPending} disabled={nome.trim().length < 3} onClick={() => save.mutate()}>Salvar</Button>
          <Button block size="lg" style={{ marginTop: 8 }} onClick={() => setView('senha')}>Alterar senha</Button>
          <Button block size="lg" style={{ marginTop: 8 }} onClick={logout}>Sair da conta</Button>
        </>
      )}
    </Sheet>
  );
}

/** Trocar de consultoria/plataforma sem sair da tela: lista todas em que a pessoa participa. */
export function TenantSwitcher({ open, onClose, currentId }: { open: boolean; onClose: () => void; currentId?: string }) {
  const me = useMe().data;
  const nav = useNavigate();
  const go = (to: string) => { onClose(); nav(to); };
  return (
    <Sheet open={open} onClose={onClose} title="Trocar de plataforma" subtitle="Consultorias em que você participa">
      <div className="stack gap-8">
        {me?.tenants.map((t, i) => {
          const on = t.id === currentId;
          return (
            <button key={t.id} className="item" style={{ padding: 13, gap: 12, borderColor: on ? 'var(--accent-border)' : undefined, background: on ? '#F4F8F6' : undefined }} onClick={() => go(`/t/${t.id}`)}>
              <Avatar name={t.nome} size={38} round={false} color={TINTS[i % TINTS.length]} />
              <div className="flex1">
                <div className="wrapline" style={{ fontSize: 14, fontWeight: 600 }}>{t.nome}</div>
                <div className="small">{t.roleLabel} · {t.meta}</div>
              </div>
              {on ? <span className="pill res">atual</span> : <span style={{ color: 'var(--accent)' }}>→</span>}
            </button>
          );
        })}
        {me?.hasShares && (
          <button className="item" style={{ padding: 13, gap: 12 }} onClick={() => go('/portal')}>
            <Avatar name="Compartilhados" size={38} round={false} color="#5F7386" />
            <div className="flex1">
              <div style={{ fontSize: 14, fontWeight: 600 }}>Compartilhados comigo</div>
              <div className="small">Relatórios que outras pessoas enviaram</div>
            </div>
            <span style={{ color: 'var(--accent)' }}>→</span>
          </button>
        )}
      </div>
    </Sheet>
  );
}

const NAV: Array<[string, string]> = [['', 'Início'], ['restaurantes', 'Restaurantes'], ['agenda', 'Agenda'], ['relatorios', 'Relatórios'], ['equipe', 'Equipe']];

export function AppShell() {
  const tid = useTid();
  const me = useMe();
  const t = useTenant(tid);
  const loc = useLocation();
  const [account, setAccount] = useState(false);
  const [switching, setSwitching] = useState(false);
  const uploadsAll = useUploads();

  if (t.error instanceof ApiError && (t.error.status === 404 || t.error.status === 403)) return <Navigate to="/plataformas" replace />;
  const info = t.data;
  const multi = (me.data?.tenants.length ?? 0) > 1 || !!me.data?.hasShares;
  // Agenda é só da equipe de nutrição (visualizador não vê).
  const nav = NAV.filter(([seg]) => seg !== 'agenda' || (info && info.role !== 'visualizador'));
  const sending = uploadsAll.filter((u) => u.status !== 'failed').length;
  const failedUp = uploadsAll.length - sending;

  const isActive = (seg: string) => {
    const rest = loc.pathname.replace(`/t/${tid}`, '').replace(/^\//, '');
    return seg === '' ? rest === '' : rest.startsWith(seg);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header className="topbar">
        <div className="topbar-in">
          {/* Toque no nome da plataforma: troca de consultoria (quando participa de mais de uma). */}
          <button
            className="row gap-12 flex1"
            style={{ color: 'inherit', border: 0, background: 'none', padding: 0, textAlign: 'left', cursor: multi ? 'pointer' : 'default' }}
            onClick={() => multi && setSwitching(true)}
            aria-label={multi ? 'Trocar de plataforma' : undefined}
          >
            {info ? (
              <Avatar name={info.tenant.nome} size={30} round={false} color={tenantTint(tid, me.data?.tenants)} />
            ) : (
              <div className="skeleton" style={{ width: 30, height: 30, borderRadius: 9 }} />
            )}
            <div className="flex1">
              <div className="tname ellipsis">{info?.tenant.nome ?? ' '}{multi && <span style={{ color: 'var(--text-2)', fontWeight: 400 }}> ▾</span>}</div>
              <div className="trole">{info?.roleLabel ?? ' '}</div>
            </div>
          </button>
          <nav className="topnav seg" aria-label="Seções">
            {nav.map(([seg, label]) => (
              <Link key={seg} to={`/t/${tid}${seg ? `/${seg}` : ''}`} className={isActive(seg) ? 'on' : ''} aria-current={isActive(seg) ? 'page' : undefined}>
                {label}
              </Link>
            ))}
          </nav>
          {multi && <button className="btn xs none hide-mobile-inline" style={{ color: 'var(--text-3)' }} onClick={() => setSwitching(true)}>Trocar</button>}
          <button className="none" style={{ border: 0, background: 'none', padding: 0 }} onClick={() => setAccount(true)} aria-label="Sua conta">
            <Avatar name={me.data?.user.nome ?? '·'} size={34} color="#22282A" />
          </button>
        </div>
      </header>

      {uploadsAll.length > 0 && (
        <div style={{ maxWidth: 1080, margin: '10px auto -6px', padding: '0 16px' }}>
          <Link to={`/t/${uploadsAll[0].tid}/visita/${uploadsAll[0].visitId}`} className="up-pill" style={failedUp && !sending ? { background: 'var(--nc-soft)', color: 'var(--nc-text)', borderColor: 'var(--nc-border)' } : undefined}>
            {sending ? <span className="spinner" style={{ width: 12, height: 12 }} /> : '!'}
            {sending ? `Enviando ${sending === 1 ? '1 foto' : `${sending} fotos`} em segundo plano` : `${failedUp === 1 ? '1 foto' : `${failedUp} fotos`} não ${failedUp === 1 ? 'foi enviada' : 'foram enviadas'} · ver`}
          </Link>
        </div>
      )}
      <main className="page anim-fade" key={loc.pathname.split('/').slice(0, 4).join('/')}>
        {info ? <Outlet context={info} /> : <Loading rows={4} height={70} />}
      </main>

      <nav className="bottomnav" aria-label="Seções">
        {nav.map(([seg, label]) => (
          <Link key={seg} to={`/t/${tid}${seg ? `/${seg}` : ''}`} className={isActive(seg) ? 'on' : ''} aria-current={isActive(seg) ? 'page' : undefined}>
            <span className="ndot" />
            {label}
          </Link>
        ))}
      </nav>
      {account && <AccountSheet open onClose={() => setAccount(false)} />}
      <TenantSwitcher open={switching} onClose={() => setSwitching(false)} currentId={tid} />
    </div>
  );
}
